/**
 * `<caos-workspace-rail>` — the narrow strip of workspaces a person belongs to. (Catalog: Conversation › WorkspaceRail — the narrow strip of tiles for switching between workspaces.)
 *
 * The outermost column of a conversation surface: one tile per workspace, the current one marked,
 * and an optional count on each saying something is waiting there. A person in three workspaces
 * needs to see, without opening any of them, which one has something in it.
 *
 * ── Why this is not the app launcher ─────────────────────────────────────────────────────────
 * `<caos-app-launcher>` opens the apps of ONE workspace. This switches between workspaces, which is
 * the level above — a different set of conversations, people and unread counts, not a different
 * screen within the same one. Both can be on screen at once and they are not alternatives.
 *
 * ── A tile is a letter, not a picture ────────────────────────────────────────────────────────
 * Each tile draws one or two characters taken from the workspace's name, or whatever the author
 * gives instead. Deliberately not an image: a rail of images is a rail that is empty while they
 * load and broken when one 404s, and the whole value of the rail is being able to hit the right
 * tile without reading. The NAME is still the accessible name and still the tooltip, so nothing
 * rests on recognising a letter.
 *
 * Choosing a tile reports and changes nothing — switching workspace is the surface's, and a rail
 * that marked itself before the switch had happened would be lying whenever it failed.
 *
 * Styling seams: `::part(rail)`, `::part(tile)`, `::part(count)`.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { applyAccessibleName } from '../accessible-name.js';
// The library already knows what letters stand for a name — `<caos-avatar>` has had to answer that
// since it shipped. Two answers would eventually differ, and this one would be the worse of them:
// the avatar takes the FIRST and LAST word, which is what a three-part name wants, and walks code
// points rather than UTF-16 units, so a name opening with an astral character does not come back as
// half of one.
import { initialsOf } from './avatar.js';

/** One workspace a person can switch to. */
export interface CaosWorkspace {
  /** Stable identity — what `select` reports and what `current` names. */
  id: string;
  /** What it is called. The accessible name and the tooltip; also the source of the tile's letters. */
  label: string;
  /** One or two characters to draw instead of the letters taken from the label. */
  badge?: string;
  /** How much is waiting there. 0 or absent ⇒ nothing is. */
  unread?: number;
}

const sheet = makeSheet(`
  :host { display: block; }

  .rail {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: var(--caos-space-2);
    padding: var(--caos-space-2);
    height: 100%;
    overflow-y: auto;
    background: var(--caos-color-surface-2);
    border-inline-end: 1px solid var(--caos-color-border);
  }

  .tile {
    position: relative;
    width: 32px;
    height: 32px;
    flex: 0 0 auto;
    appearance: none;
    border: 1px solid var(--caos-color-border);
    border-radius: var(--caos-radius-sm);
    background: var(--caos-color-surface);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font);
    font-size: var(--caos-text-label);
    font-weight: var(--caos-font-weight-label);
    text-transform: uppercase;
    cursor: pointer;
  }
  .tile:hover { color: var(--caos-color-text); }
  .tile:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
  .tile[aria-current="true"] {
    background: var(--caos-color-accent-fill);
    color: var(--caos-color-accent-contrast);
    border-color: var(--caos-color-accent-fill);
  }

  .count {
    position: absolute;
    inset-block-start: -4px;
    inset-inline-end: -4px;
    min-width: 16px;
    padding: 0 4px;
    border-radius: var(--caos-radius-pill);
    /* The FILL pair, not the lettering token: --caos-color-danger is the colour danger text is
       drawn IN, and text drawn ON it comes out at 3.27:1 in the dark theme. Same pair the
       destructive button uses. */
    background: var(--caos-color-danger-fill);
    color: var(--caos-color-danger-contrast);
    font-size: 10px;
    line-height: 16px;
  }
`);

export class CaosWorkspaceRail extends CaosElement {
  static get observedAttributes(): string[] {
    return ['current', 'aria-label'];
  }

  private readonly rail: HTMLDivElement;
  private _workspaces: CaosWorkspace[] = [];

  constructor() {
    super(sheet);
    this.rail = document.createElement('div');
    this.rail.className = 'rail';
    this.rail.setAttribute('part', 'rail');
    this.rail.setAttribute('role', 'list');
    this.root.append(this.rail);
  }

  connectedCallback(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'navigation');
    this.syncName();
    this.render();
  }

  attributeChangedCallback(name: string): void {
    if (name === 'aria-label') {
      this.syncName();
      return;
    }
    this.render();
  }

  private syncName(): void {
    applyAccessibleName(this.rail, this.getAttribute('aria-label'), 'Workspaces');
  }

  /** The workspaces, in the order they should read. */
  get workspaces(): CaosWorkspace[] {
    return this._workspaces;
  }
  set workspaces(list: readonly CaosWorkspace[]) {
    this._workspaces = [...list];
    this.render();
  }

  /** Which workspace is open, by id. */
  get current(): string {
    return this.getAttribute('current') ?? '';
  }
  set current(value: string) {
    this.setAttribute('current', value);
  }

  private render(): void {
    this.rail.replaceChildren();
    for (const workspace of this._workspaces) {
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'tile';
      tile.setAttribute('part', 'tile');
      tile.setAttribute('role', 'listitem');
      tile.dataset['workspaceId'] = workspace.id;
      if (workspace.id === this.current) tile.setAttribute('aria-current', 'true');

      const letters = workspace.badge ?? initialsOf(workspace.label);
      const glyph = document.createElement('span');
      glyph.textContent = letters;
      // The letters are a shorthand for the name, which is already the accessible name — read out
      // they would say the same thing twice, the second time as nonsense.
      glyph.setAttribute('aria-hidden', 'true');
      tile.append(glyph);

      const unread = workspace.unread ?? 0;
      if (unread > 0) {
        const count = document.createElement('span');
        count.className = 'count';
        count.setAttribute('part', 'count');
        count.textContent = unread > 99 ? '99+' : String(unread);
        count.setAttribute('aria-hidden', 'true');
        tile.append(count);
      }

      // The name carries the count, so a screen-reader user gets the same two facts a sighted one
      // gets from the tile and the number on it — rather than the name alone.
      const name = unread > 0 ? `${workspace.label}, ${unread} unread` : workspace.label;
      applyAccessibleName(tile, name, workspace.label);
      tile.title = workspace.label;

      tile.addEventListener('click', () => {
        this.dispatchEvent(
          new CustomEvent('select', { bubbles: true, composed: true, detail: { id: workspace.id } }),
        );
      });
      this.rail.append(tile);
    }
  }
}

define('caos-workspace-rail', CaosWorkspaceRail);

declare global {
  interface HTMLElementTagNameMap {
    'caos-workspace-rail': CaosWorkspaceRail;
  }
}
